import React, { useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Button, Card, Input, Text } from '@fragments';
import { IconArrowLeft, IconArrowRight, IconArrowUp, IconCheck, IconPencil, IconPlus, IconSparkles, IconX } from '@tabler/icons-react';
import StudioAi from '../../../StudioAi';
import OptionPicker from '../OptionPicker';
import ReplyStep from './components/ReplyStep';
import useOnboardingReply from './hooks/useOnboardingReply';
import './styles.scss';

const EASE = [0.22, 1, 0.36, 1];
// Steps slide in reading order: forward to the next question, back to one being edited.
const slide = reduced => ({
  enter: direction => ({ opacity: 0, x: reduced ? 0 : direction * 32 }),
  center: { opacity: 1, x: 0 },
  exit: direction => ({ opacity: 0, x: reduced ? 0 : direction * -32 }),
});
// The input and the options take each other's place while the surface morphs around them.
const swap = reduced => ({ initial: { opacity: 0, scale: reduced ? 1 : 0.985 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: reduced ? 1 : 0.985 } });

// The direction of the latest step change, kept until the next one.
function useDirection(order) {
  const last = useRef(order);
  const direction = useRef(1);
  if (order !== last.current) { direction.current = order > last.current ? 1 : -1; last.current = order; }
  return direction.current;
}

// The Studio asking: its orb, the question, and what the analysis suggests.
function Ask({ id, prompt, thinking, suggestion, t }) {
  return <div className="studio-onboarding-ask">
    <span className="studio-onboarding-ask__orb"><StudioAi size={22} active={thinking} /></span>
    <div className="studio-onboarding-ask__body">
      <Text.p id={id} contrast={9}>{prompt}</Text.p>
      {suggestion && <div className="studio-onboarding-ask__suggestion">
        <Text.caption contrast={6} left={<IconSparkles aria-hidden="true" />}>{t('onboardingHypothesis')}</Text.caption>
        <Text.label contrast={9}>{suggestion}</Text.label>
      </div>}
    </div>
  </div>;
}

// The prompt input as the reply's free-text answer.
function ReplyInput({ value, onInput, label, placeholder, maxLength, disabled, onEnter, back, submit }) {
  return <Card className="studio-composer" noBodyPadding cornerRadius="large" flat>
    <div className="studio-composer__entry">
      <Input value={value} onInput={onInput} multiline rows={1} maxLength={maxLength} disabled={disabled} aria-label={label} placeholder={placeholder}
        onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); onEnter?.(); }
        }} />
    </div>
    <div className="studio-composer__toolbar">
      <div className="studio-composer__tools">{back}</div>
      {submit}
    </div>
  </Card>;
}

function QuestionStep({ studio: s, view, setView, reduced }) {
  const { t, onboarding: o } = s;
  const r = useOnboardingReply(o);
  const { q } = r;
  const busy = o.busy;
  const promptId = `studio-onboarding-q-${q.id}`;
  const toOptions = () => setView('options');
  const add = () => { if (r.addCustom()) toOptions(); };
  const pick = value => {
    if (value !== 'correct') return r.confirm();
    r.correct();
    setView('input');
    return null;
  };

  const options = r.choice ? <>
    <OptionPicker type={q.multiple ? 'multiple' : 'single'} options={r.rows} labelledBy={promptId} disabled={busy} onPick={r.toggle} />
    <div className="studio-onboarding-step__footer">
      {r.canType
        ? <Button className="studio-plain-control" variant="rounded" color="overlay" size="medium" startIcon={<IconPlus />} preventLoading disabled={busy} onClick={() => setView('input')}>{t('onboardingAddOwn')}</Button>
        : <span />}
      <Button variant="rounded" color="inverse" size="medium" endIcon={<IconArrowRight />} preventLoading disabled={!r.canSend} onClick={r.send}>{t('onboardingContinue')}</Button>
    </div>
  </> : <OptionPicker labelledBy={promptId} disabled={busy} pending={r.picked} onPick={pick} options={[
    r.canConfirm && { value: 'confirm', label: t('onboardingConfirm'), icon: <IconCheck /> },
    r.canType && { value: 'correct', label: t('onboardingCorrect'), icon: <IconPencil /> },
  ].filter(Boolean)} />;

  const hasOptions = r.choice || r.canConfirm;
  const input = <ReplyInput value={r.text} onInput={r.setText} maxLength={q.max_characters || 500} disabled={busy || !r.canType} label={q.prompt}
    placeholder={t(r.choice ? 'onboardingCustomPlaceholder' : r.canConfirm ? 'onboardingCorrectionPlaceholder' : 'onboardingAnswerPlaceholder')}
    onEnter={r.choice ? add : r.send}
    back={hasOptions && <Button className="studio-plain-control" variant="rounded" color="overlay" size="medium" startIcon={<IconArrowLeft />} preventLoading disabled={busy} onClick={toOptions}>{t('backToChoices')}</Button>}
    submit={r.choice
      ? <Button className="studio-submit" variant="rounded" color="inverse" size="medium" endIcon={<IconPlus />} preventLoading disabled={busy || !r.text.trim()} aria-label={t('onboardingAdd')} onClick={add}>{t('onboardingAdd')}</Button>
      : <Button className="studio-submit" variant="rounded" color="inverse" size="medium" endIcon={<IconArrowUp />} disabled={!r.canSend} aria-label={t('sendShort')} onClick={r.send}>{t('sendShort')}</Button>} />;

  return <div className="studio-onboarding-step">
    <Ask id={promptId} prompt={q.prompt} thinking={busy} suggestion={r.suggestion} t={t} />
    <div className="studio-onboarding-step__answer">
      <AnimatePresence mode="popLayout" initial={false}>
        <ReplyStep key={view} className="studio-onboarding-step__view" {...swap(reduced)} transition={{ duration: reduced ? 0 : 0.3, ease: EASE }}>
          {view === 'input' ? input : options}
        </ReplyStep>
      </AnimatePresence>
    </div>
  </div>;
}

// The last question (memory) stays in view, disabled, while the profile is created.
function MemoryStep({ studio: s }) {
  const { t, onboarding: o } = s;
  const [picked, setPicked] = useState(null);
  const completing = o.stage === 'completing';
  const pick = value => { setPicked(value); return o.complete(value === 'yes'); };
  return <div className="studio-onboarding-step">
    <Ask id="studio-onboarding-memory" prompt={t('onboardingMemoryQuestion')} thinking={completing} t={t} />
    <OptionPicker labelledBy="studio-onboarding-memory" disabled={o.busy || completing} pending={o.busy || completing ? picked : null} onPick={pick} options={[
      { value: 'yes', label: t('onboardingMemoryYes'), icon: <IconCheck /> },
      { value: 'no', label: t('onboardingMemoryNo'), icon: <IconX /> },
    ]} />
  </div>;
}

// The onboarding's side of the composer: the question, its suggestion and the ways
// to answer it, in one surface. A choice or a suggestion opens on the options
// (the composer narrows into a picker); free text opens on the input. Each
// question slides in after the previous one.
export default function OnboardingReply({ studio: s, view, setView }) {
  const { t, onboarding: o } = s;
  const { stage, busy, question: q } = o;
  const reduced = useReducedMotion();
  const waiting = stage === 'analyzing' || stage === 'completing';
  const direction = useDirection(stage === 'analyzing' ? 0 : stage === 'questioning' ? o.questionNumber : o.total + 1);
  const stepKey = stage === 'questioning' ? `question-${q.id}` : stage === 'completing' ? 'memory' : stage;

  const headingText = waiting ? t(stage === 'analyzing' ? 'onboardingAnalyzingHeading' : 'onboardingCompleting')
    : stage === 'memory' ? t('onboardingLastStep')
    : t(o.editing ? 'onboardingEditing' : 'onboardingQuestionCount', { current: o.questionNumber, total: o.total });
  const heading = waiting
    ? <Text.caption className="studio-ai-heading" left={<IconSparkles aria-hidden="true" />}>{headingText}</Text.caption>
    : <Text.caption id="studio-onboarding-step" contrast={6}>{headingText}</Text.caption>;
  const side = stage !== 'questioning' ? null
    : o.editing ? <Button className="studio-plain-control" variant="rounded" color="overlay" size="small" preventLoading disabled={busy} onClick={o.cancelEdit}>{t('cancel')}</Button>
    : q?.allowed_responses.includes('skip') ? <Button className="studio-plain-control" variant="rounded" color="overlay" size="small" disabled={busy} onClick={() => o.answer(q, { kind: 'skip' })}>{t('onboardingSkip')}</Button>
    : null;

  const body = stage === 'questioning' ? <QuestionStep studio={s} view={view} setView={setView} reduced={reduced} />
    : stage === 'analyzing' ? <ReplyInput value="" onInput={() => {}} disabled label={t('onboardingAnalyzingPlaceholder')} placeholder={t('onboardingAnalyzingPlaceholder')}
      submit={<Button className="studio-submit" variant="rounded" color="inverse" size="medium" endIcon={<IconArrowUp />} disabled aria-label={t('sendShort')}>{t('sendShort')}</Button>} />
    : <MemoryStep studio={s} />;

  return <section className="studio-onboarding-reply" aria-labelledby={waiting ? undefined : 'studio-onboarding-step'} aria-busy={busy || waiting}>
    <div className="studio-onboarding-reply__heading">
      <motion.span key={headingText} className="studio-onboarding-reply__count" initial={{ opacity: 0, y: reduced ? 0 : 3 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduced ? 0 : 0.2, ease: EASE }}>{heading}</motion.span>
      {side}
    </div>
    <div className="studio-onboarding-reply__stage">
      <AnimatePresence mode="popLayout" initial={false} custom={direction}>
        <ReplyStep key={stepKey} className="studio-onboarding-reply__step" custom={direction} variants={slide(reduced)} initial="enter" animate="center" exit="exit"
          transition={{ duration: reduced ? 0 : 0.28, ease: EASE }}>
          {body}
        </ReplyStep>
      </AnimatePresence>
    </div>
  </section>;
}
